/**
 * Universal Notification System - Notification Handler Utilities
 * 
 * This module handles the display and interaction of notifications with the UI.
 * Provides functions for formatting notifications, showing toasts, and handling clicks.
 */

import { message } from 'antd';
import adminNotificationRoutes from '../config/adminNotificationRoutes';
import { 
  getNotificationPriority, 
  getAutoDismissDuration, 
  getNotificationSound 
} from './notificationPriority';

/**
 * Handle notification click - navigate to relevant page
 * @param {Object} notification - Notification object (should have type or event field)
 * @param {Function} navigate - React Router navigate function
 */
export const handleNotificationClick = (notification, navigate) => {
  try {
    // Support both 'type' and 'event' field names
    const eventType = notification.type || notification.event;
    
    if (!eventType) {
      console.warn('Notification missing event type:', notification);
      message.warning('Unable to navigate: notification missing event type');
      return;
    }
    
    const routeConfig = adminNotificationRoutes[eventType];
    
    if (routeConfig && routeConfig.web) {
      // Get the base route
      let path = routeConfig.web;
      
      // Get payload/data from notification
      const payload = notification.payload || notification.data || {};
      
      // Replace dynamic parameters in path
      if (routeConfig.params && routeConfig.params.length > 0) {
        routeConfig.params.forEach(param => {
          // Try different parameter name variations
          const paramValue = payload[param] || 
                           payload[`${param}_id`] || 
                           payload[`${param}Id`] ||
                           payload.id;
          
          if (paramValue) {
            path = path.replace(`:${param}`, paramValue);
          }
        });
        
        // Also handle common parameter replacements
        if (payload.appointment_id) {
          path = path.replace(':appointment_id', payload.appointment_id);
        }
        if (payload.pa_id) {
          path = path.replace(':pa_id', payload.pa_id);
        }
        if (payload.checkin_id || payload.encounter_id) {
          path = path.replace(':checkin_id', payload.checkin_id || payload.encounter_id);
          path = path.replace(':encounter_id', payload.checkin_id || payload.encounter_id);
        }
        if (payload.claim_id) {
          path = path.replace(':claim_id', payload.claim_id);
        }
      }
      
      // Navigate to the page
      navigate(path);
      
      // Execute any additional action if defined
      if (routeConfig.action && typeof routeConfig.action === 'function') {
        routeConfig.action(notification);
      }
    } else {
      console.warn('No route configured for event type:', eventType);
      message.warning('Unable to navigate to the requested page');
    }
  } catch (error) {
    console.error('Error handling notification click:', error);
    message.error('An error occurred while navigating');
  }
};

/**
 * Show notification toast
 * @param {Object} notification - Notification object
 * @param {Object} priorityConfig - Priority configuration
 */
export const showNotificationToast = (notification, priorityConfig) => {
  try {
    const title = getAdminNotificationTitle(notification.eventType, notification.data);
    const description = getAdminNotificationDescription(notification.eventType, notification.data);
    
    // Determine message type based on priority
    const messageType = priorityConfig.messageType || 'info';
    
    // Show Ant Design message
    message[messageType]({
      content: (
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          {description && (
            <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
              {description}
            </div>
          )}
        </div>
      ),
      duration: priorityConfig.duration || 4.5,
    });
    
    // Play sound if enabled
    if (priorityConfig.sound && typeof priorityConfig.sound === 'string') {
      try {
        const audio = new Audio(priorityConfig.sound);
        audio.volume = 0.3;
        audio.play().catch((err) => {
          console.log('Unable to play notification sound:', err);
        });
      } catch (err) {
        console.log('Unable to play notification sound:', err);
      }
    }
  } catch (error) {
    console.error('Error showing notification toast:', error);
  }
};

/**
 * Format notification title based on event type
 * @param {string} eventType - Event type
 * @param {Object} data - Notification data/payload
 * @returns {string} Formatted title
 */
export const getAdminNotificationTitle = (eventType, data = {}) => {
  const titles = {
    // Appointments
    'appointment.booked': 'New Appointment Booked',
    'appointment.confirmed': 'Appointment Confirmed',
    'appointment.cancelled': 'Appointment Cancelled',
    'appointment.converted': 'Appointment Converted',
    'appointment.expired': 'Appointment Expired',
    
    // PA (Pre-Authorization)
    'pa.created': 'New Pre-Authorization Request',
    'pa.approved': 'Pre-Authorization Approved',
    'pa.rejected': 'Pre-Authorization Rejected',
    'pa.query_responded': 'PA Query Responded',
    'pa.flagged': 'Pre-Authorization Flagged',
    
    // Encounters/Check-ins
    'encounter.created': 'New Encounter Created',
    'encounter.updated': 'Encounter Updated',
    
    // Claims
    'claim.paid': 'Claim Paid',
    'claim.approved': 'Claim Approved',
    'claim.rejected': 'Claim Rejected',
    
    // System
    'system.alert': 'System Alert',
    'system.maintenance': 'System Maintenance',
    'system.broadcast': 'System Broadcast',
    'system.app_update': 'App Update Available',
    'system.emergency': 'Emergency Alert',
  };
  
  // If title is provided in data, use it
  if (data?.title) {
    return data.title;
  }
  
  return titles[eventType] || 'New Notification';
};

/**
 * Format notification description based on event type
 * @param {string} eventType - Event type
 * @param {Object} data - Notification data/payload
 * @returns {string} Formatted description
 */
export const getAdminNotificationDescription = (eventType, data = {}) => {
  // If body/description is provided in data, use it
  if (data?.body || data?.description || data?.message) {
    return data.body || data.description || data.message;
  }
  
  const payload = data?.payload || data;
  
  switch (eventType) {
    // Appointments
    case 'appointment.booked':
      return `Appointment scheduled for ${payload.patient_name || 'patient'}${payload.scheduled_start ? ` on ${new Date(payload.scheduled_start).toLocaleDateString()}` : ''}`;
    case 'appointment.confirmed':
      return `Appointment confirmed${payload.appointment_id ? ` (#${payload.appointment_id})` : ''}`;
    case 'appointment.cancelled':
      return `Appointment cancelled${payload.appointment_id ? ` (#${payload.appointment_id})` : ''}${payload.reason ? `: ${payload.reason}` : ''}`;
    case 'appointment.converted':
      return `Appointment converted to check-in${payload.checkin_id ? ` (#${payload.checkin_id})` : ''}`;
    case 'appointment.expired':
      return `Appointment expired${payload.appointment_id ? ` (#${payload.appointment_id})` : ''}`;
    
    // PA (Pre-Authorization)
    case 'pa.created':
      return `New PA request created${payload.pa_id ? ` (#${payload.pa_id})` : ''}${payload.patient_name ? ` for ${payload.patient_name}` : ''}`;
    case 'pa.approved':
      return `PA request approved${payload.pa_id ? ` (#${payload.pa_id})` : ''}`;
    case 'pa.rejected':
      return `PA request rejected${payload.pa_id ? ` (#${payload.pa_id})` : ''}${payload.reason ? `: ${payload.reason}` : ''}`;
    case 'pa.query_responded':
      return `Query responded for PA${payload.pa_id ? ` (#${payload.pa_id})` : ''}`;
    case 'pa.flagged':
      return `PA request flagged${payload.pa_id ? ` (#${payload.pa_id})` : ''}${payload.reason ? `: ${payload.reason}` : ''}`;
    
    // Encounters/Check-ins
    case 'encounter.created':
      return `New encounter created${payload.checkin_id || payload.encounter_id ? ` (#${payload.checkin_id || payload.encounter_id})` : ''}`;
    case 'encounter.updated':
      return `Encounter updated${payload.checkin_id || payload.encounter_id ? ` (#${payload.checkin_id || payload.encounter_id})` : ''}`;
    
    // Claims
    case 'claim.paid':
      return `Claim paid${payload.claim_id ? ` (#${payload.claim_id})` : ''}${payload.amount ? ` - $${payload.amount}` : ''}`;
    case 'claim.approved':
      return `Claim approved${payload.claim_id ? ` (#${payload.claim_id})` : ''}`;
    case 'claim.rejected':
      return `Claim rejected${payload.claim_id ? ` (#${payload.claim_id})` : ''}${payload.reason ? `: ${payload.reason}` : ''}`;
    
    // System
    case 'system.alert':
      return payload.message || 'System alert issued';
    case 'system.maintenance':
      return payload.message || 'System maintenance scheduled';
    case 'system.broadcast':
      return payload.message || 'System broadcast message';
    case 'system.app_update':
      return payload.message || payload.body || 'A new app version is available';
    case 'system.emergency':
      return payload.message || payload.body || 'Emergency system alert';
    
    default:
      return payload.message || data.message || '';
  }
};

/**
 * Get priority configuration for event type
 * Uses notificationPriority utility for consistent mapping
 * @param {string} eventType - Event type
 * @returns {Object} Priority configuration
 */
export const getAdminNotificationPriority = (eventType) => {
  const priorityConfig = getNotificationPriority(eventType);
  
  // Map priority to Ant Design message type
  const messageTypeMap = {
    urgent: 'error',
    high: 'warning',
    medium: 'info',
    low: 'success',
  };
  
  return {
    priority: priorityConfig.priority,
    messageType: messageTypeMap[priorityConfig.priority] || 'info',
    duration: getAutoDismissDuration(eventType),
    sound: getNotificationSound(eventType),
    ...priorityConfig,
  };
};

/**
 * Format notification time relative to now
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted time string
 */
export const formatNotificationTime = (timestamp) => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting notification time:', error);
    return '';
  }
};

/**
 * Get notification icon for event type
 * @param {string} eventType - Event type
 * @returns {string} Icon name or emoji
 */
export const getNotificationIcon = (eventType) => {
  const icons = {
    'CLAIM_SUBMITTED': '📄',
    'CLAIM_APPROVED': '✅',
    'CLAIM_REJECTED': '❌',
    'CLAIM_PROCESSING': '⏳',
    'CLAIM_DOCUMENT_REQUESTED': '📎',
    'CLAIM_STATUS_UPDATED': '🔄',
    'POLICY_CREATED': '📋',
    'POLICY_RENEWED': '🔄',
    'POLICY_EXPIRED': '⚠️',
    'POLICY_CANCELLATION_REQUESTED': '🚫',
    'PAYMENT_RECEIVED': '💰',
    'PAYMENT_FAILED': '❌',
    'PAYMENT_OVERDUE': '⚠️',
    'USER_REGISTERED': '👤',
    'USER_STATUS_CHANGED': '🔄',
    'SYSTEM_ANNOUNCEMENT': '📢',
    'ALERT_HIGH_PRIORITY': '🔴',
  };
  
  return icons[eventType] || '🔔';
};

/**
 * Get notification color for event type
 * @param {string} eventType - Event type
 * @returns {string} CSS color
 */
export const getNotificationColor = (eventType) => {
  const colors = {
    'CLAIM_SUBMITTED': '#1890ff',
    'CLAIM_APPROVED': '#52c41a',
    'CLAIM_REJECTED': '#f5222d',
    'CLAIM_PROCESSING': '#1890ff',
    'CLAIM_DOCUMENT_REQUESTED': '#faad14',
    'CLAIM_STATUS_UPDATED': '#1890ff',
    'POLICY_CREATED': '#52c41a',
    'POLICY_RENEWED': '#52c41a',
    'POLICY_EXPIRED': '#f5222d',
    'POLICY_CANCELLATION_REQUESTED': '#f5222d',
    'PAYMENT_RECEIVED': '#52c41a',
    'PAYMENT_FAILED': '#f5222d',
    'PAYMENT_OVERDUE': '#f5222d',
    'USER_REGISTERED': '#1890ff',
    'USER_STATUS_CHANGED': '#1890ff',
    'SYSTEM_ANNOUNCEMENT': '#722ed1',
    'ALERT_HIGH_PRIORITY': '#f5222d',
  };
  
  return colors[eventType] || '#1890ff';
};

/**
 * Get category from event type
 * @param {string} eventType - Event type
 * @returns {string} Category name
 */
export const getCategoryFromEventType = (eventType) => {
  if (!eventType) return 'system';
  
  // Map event types to categories
  if (eventType.startsWith('appointment.')) {
    return 'appointments';
  }
  if (eventType.startsWith('pa.')) {
    return 'pa';
  }
  if (eventType.startsWith('encounter.')) {
    return 'encounters';
  }
  if (eventType.startsWith('claim.')) {
    return 'claims';
  }
  if (eventType.startsWith('system.')) {
    return 'system';
  }
  
  // Fallback to system category
  return 'system';
};

/**
 * Alias for getAdminNotificationTitle - for compatibility with existing code
 * @param {string} eventType - Event type
 * @param {Object} data - Notification data
 * @returns {string} Formatted title
 */
export const getNotificationTitle = getAdminNotificationTitle;

/**
 * Alias for getAdminNotificationDescription - for compatibility with existing code
 * @param {string} eventType - Event type
 * @param {Object} data - Notification data
 * @returns {string} Formatted description
 */
export const getNotificationDescription = getAdminNotificationDescription;

/**
 * Alias for showNotificationToast - for compatibility with existing code
 * @param {Object} notification - Notification object
 * @param {Object} priorityConfig - Priority configuration
 */
export const showToastNotification = showNotificationToast;

/**
 * Process incoming notification
 * @param {Object} notification - Raw notification from WebSocket
 * @param {Function} navigate - React Router navigate function
 * @param {Function} onNotificationClick - Callback when notification is clicked
 */
export const processNotification = (notification, navigate, onNotificationClick) => {
  try {
    const priorityConfig = getAdminNotificationPriority(notification.eventType);
    
    // Show toast notification
    showNotificationToast(notification, priorityConfig);
    
    // Handle click for deep linking
    if (onNotificationClick) {
      onNotificationClick(notification);
    } else {
      handleNotificationClick(notification, navigate);
    }
    
    return priorityConfig;
  } catch (error) {
    console.error('Error processing notification:', error);
  }
};

export default {
  handleNotificationClick,
  showNotificationToast,
  getAdminNotificationTitle,
  getAdminNotificationDescription,
  getAdminNotificationPriority,
  formatNotificationTime,
  getNotificationIcon,
  getNotificationColor,
  processNotification,
};

